import React from 'react';
import { Box, Group, SegmentedControl, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { abvText, axisIndex, BEER_AXES, BEER_ITEMS, BEER_TASTES, beerColor, BeerItem, beerRanges, beerRatings, beerTaste, findBeers, inkOn, stylesOf, Tasted } from '../../utils/beerGuide';
import { ScanLine } from './scanTypes';
// The yakiniku view's chart and list, with beer's own additions on top
import classes from './YakinikuView.module.css';
import beer from './BeerView.module.css';

/**
 * A menu line about a beer, and the styles it names (a flight can list
 * several). No `items`: Jev says it's about a beer, but it names no style
 * (a brewery, a description, a beer sold by its brand).
 */
export interface BeerLine {
  index: number;
  line: ScanLine;
  items: BeerItem[];
}

/**
 * The lines about beer. A line Jev judged not to be is `dropped` and never
 * counts, whatever it prints; a line that names no style counts only when Jev
 * said it's about a beer (not when Jev couldn't be asked).
 */
export function beerLines(lines: ScanLine[]): BeerLine[] {
  const out: BeerLine[] = [];
  lines.forEach((line, index) => {
    if (line.dropped) return;
    const named = [...new Set(findBeers(line.t).flatMap((h) => h.keys))].map((k) => BEER_ITEMS.get(k)!);
    // "West Coast IPA, a classic IPA" is one style: the word adds nothing next to a style it covers
    const items = named.filter((it) => it.kind === 'style' || !it.styles.some((k) => named.some((n) => n.key === k)));
    if (!items.length && line.jev == null) return;
    out.push({ index, line, items });
  });
  return out;
}

const BITTER = axisIndex('bitter'), BODY = axisIndex('body');

/** Where a name sits on the chart. A style has its own ratings; a word like IPA is several styles, placed at their average. */
interface Rated { at: [number, number]; area: boolean; color: string }

function rate(it: BeerItem): Rated {
  const a = beerRatings(it);
  return { at: [a[BITTER], a[BODY]], area: it.kind === 'word', color: beerColor(it) };
}

// Chart geometry, in viewBox units. Both ratings run 0–5; the body scale runs
// past both ends so the quadrant names clear the dots.
const W = 340, H = 320;
const M = { l: 30, r: 10, t: 10, b: 32 };
const X0 = -0.5, X1 = 5.5, Y0 = -0.2, Y1 = 6.1;
const sx = (v: number) => M.l + ((v - X0) / (X1 - X0)) * (W - M.l - M.r);
const sy = (v: number) => H - M.b - ((v - Y0) / (Y1 - Y0)) * (H - M.t - M.b);
const DOT = 10;

/** A numbered dot the colour of the beer, centred on 0,0. `on`: the selected line's, ringed, since the dot keeps the beer's colour. */
const Dot: React.FC<{ color: string; n: number; on?: boolean }> = ({ color, n, on }) => (
  <>
    {on && <circle r={DOT + 3} className={beer.halo} />}
    <circle r={DOT} className={beer.disc} style={{ fill: color }} />
    <text textAnchor="middle" dominantBaseline="central" className={classes.dotText} style={{ fill: inkOn(color) }}>{n}</text>
  </>
);

/** The same dot outside the chart: beside a name in the list, or over one in a taste. */
const DotKey: React.FC<{ color: string; n: number; on?: boolean; size?: number }> = ({ size = 2 * DOT + 2, ...dot }) => (
  <svg className={beer.key} viewBox={`${-DOT - 1} ${-DOT - 1} ${2 * DOT + 2} ${2 * DOT + 2}`} width={size} height={size} aria-hidden="true">
    <Dot {...dot} />
  </svg>
);

/** Dots that share a spot sit side by side, three to a row, instead of on top of each other. */
function offsets(n: number): [number, number][] {
  const PER = 3, rows = Math.ceil(n / PER), step = 2 * DOT + 1;
  return Array.from({ length: n }, (_, k) => {
    const row = Math.floor(k / PER), inRow = Math.min(PER, n - row * PER);
    return [((k % PER) - (inRow - 1) / 2) * step, (row - (rows - 1) / 2) * step];
  });
}

/** Filled up to lo; lightly up to hi, when it depends on the style. */
const Pips: React.FC<{ range: [number, number]; label: string }> = ({ range: [lo, hi], label }) => (
  <span className={classes.pips} role="img" aria-label={lo === hi ? `${label} ${lo} out of 5` : `${label} ${lo} to ${hi} out of 5, depending on the style`}>
    {[1, 2, 3, 4, 5].map((p) => <span key={p} className={`${classes.pip} ${p <= lo ? classes.pipOn : p <= hi ? classes.pipMaybe : ''}`} />)}
  </span>
);

/** A style the menu names: where it sits on the chart, the taste it's grouped under, its number, and the first line that names it */
interface Point extends Rated, Tasted { item: BeerItem; n: number; index: number }

/** How the styles are laid out above the list: on the chart, or grouped by taste */
export type BeerLayout = 'chart' | 'taste';

interface LayoutProps {
  points: Point[];
  /** The styles the selected line names */
  lit: Set<string>;
  onPick: (index: number) => void;
}

/** Every style on a bitterness × body chart. A word like IPA sits at its styles' average. */
const Chart: React.FC<LayoutProps> = ({ points, lit, onPick }) => {
  // Dots that land on the same spot line up
  const spots = new Map<string, Point[]>();
  points.forEach((p) => {
    const key = `${p.at[0].toFixed(1)},${p.at[1].toFixed(1)}`;
    spots.set(key, [...(spots.get(key) || []), p]);
  });
  const dots = [...spots.values()].flatMap((group) => {
    const off = offsets(group.length);
    return group.map((p, k) => ({ p, x: sx(p.at[0]) + off[k][0], y: sy(p.at[1]) + off[k][1] }));
  });
  // Words under styles, and the selected line's dots on top
  dots.sort((a, b) => Number(lit.has(a.p.item.key)) - Number(lit.has(b.p.item.key)) || Number(b.p.area) - Number(a.p.area));

  const midX = sx(2.5), midY = sy(2.5);
  const bitter = BEER_AXES[BITTER].label, body = BEER_AXES[BODY].label;
  const span = (it: BeerItem, k: number) => [...new Set(beerRanges(it)[k])].join('–');

  return (
    <svg className={classes.chart} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`The beer styles by ${bitter.toLowerCase()} and ${body.toLowerCase()}; the list below has the same numbers`}>
      {/* Quadrants */}
      <rect x={M.l} y={M.t} width={W - M.l - M.r} height={H - M.t - M.b} className={classes.plot} />
      <line x1={midX} x2={midX} y1={M.t} y2={H - M.b} className={classes.mid} />
      <line x1={M.l} x2={W - M.r} y1={midY} y2={midY} className={classes.mid} />
      <text x={W - M.r - 6} y={M.t + 14} textAnchor="end" className={classes.quad}>Full &amp; bitter</text>
      <text x={M.l + 6} y={M.t + 14} className={classes.quad}>Full &amp; mild</text>
      <text x={W - M.r - 6} y={H - M.b - 8} textAnchor="end" className={classes.quad}>Light &amp; bitter</text>
      <text x={M.l + 6} y={H - M.b - 8} className={classes.quad}>Light &amp; mild</text>

      {/* Axes: ticks at each rating, names along the edges */}
      {[0, 1, 2, 3, 4, 5].map((v) => <text key={`x${v}`} x={sx(v)} y={H - M.b + 13} textAnchor="middle" className={classes.tick}>{v}</text>)}
      {[0, 1, 2, 3, 4, 5].map((v) => <text key={`y${v}`} x={M.l - 6} y={sy(v)} textAnchor="end" dominantBaseline="central" className={classes.tick}>{v}</text>)}
      <text x={(M.l + W - M.r) / 2} y={H - 3} textAnchor="middle" className={classes.axis}>{bitter} →</text>
      <text transform={`translate(10 ${(M.t + H - M.b) / 2}) rotate(-90)`} textAnchor="middle" className={classes.axis}>{body} →</text>

      {dots.map(({ p, x, y }) => (
        <g
          key={p.item.key}
          className={`${classes.dot} ${beer.mark}`}
          transform={`translate(${x} ${y})`}
          onClick={() => onPick(p.index)}
          role="button"
          tabIndex={0}
          aria-label={`${p.n}. ${p.item.n}${p.area ? ', several styles' : ''}`}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(p.index); } }}
        >
          <title>{`${p.item.n}: ${bitter.toLowerCase()} ${span(p.item, BITTER)}, ${body.toLowerCase()} ${span(p.item, BODY)}`}</title>
          {/* A bigger, invisible circle so small dots are easy to tap */}
          <circle r={DOT + 6} className={classes.hit} />
          <Dot color={p.color} n={p.n} on={lit.has(p.item.key)} />
        </g>
      ))}
    </svg>
  );
};

/**
 * Every style under the taste that most sets it apart (hoppy, fruity, malty,
 * roasty, sour, or crisp when none does), the most intense first. Only the
 * tastes the menu has, lightest first.
 */
const Tastes: React.FC<LayoutProps> = ({ points, lit, onPick }) => (
  <div className={beer.tastes}>
    {BEER_TASTES.map((taste) => {
      const on = points.filter((p) => p.taste === taste).sort((a, b) => b.level - a.level);
      if (!on.length) return null;
      return (
        <section key={taste.k} className={beer.taste} aria-labelledby={`beer-taste-${taste.k}`}>
          <Text id={`beer-taste-${taste.k}`} component="h3" fw={700} size="sm" m={0}>{taste.label}</Text>
          <Text size="xs" c="dimmed">{taste.hint}</Text>
          <ul className={beer.tasteDots}>
            {on.map((p) => (
              <li key={p.item.key}>
                <UnstyledButton
                  className={`${beer.tile} ${beer.mark}`}
                  onClick={() => onPick(p.index)}
                  aria-current={lit.has(p.item.key)}
                  aria-label={`${p.n}. ${p.item.n}${p.area ? ', several styles' : ''}`}
                >
                  <DotKey color={p.color} n={p.n} on={lit.has(p.item.key)} size={28} />
                  <span className={beer.tag}>{p.item.n}</span>
                </UnstyledButton>
              </li>
            ))}
          </ul>
        </section>
      );
    })}
  </div>
);

interface BeerViewProps {
  /** The lines that name a style */
  items: BeerLine[];
  selected: number;
  onPick: (index: number) => void;
  layout: BeerLayout;
  onLayout: (layout: BeerLayout) => void;
}

/**
 * The beer view of a scanned menu: every style a line names, as a dot the
 * colour of the beer, numbered the same in the list below. A switch lays the
 * dots out on a bitterness × body chart, or groups them by taste. Tapping a dot
 * opens the first line that names it.
 */
export const BeerView: React.FC<BeerViewProps> = ({ items: styled, selected, onPick, layout, onLayout }) => {
  // Each name once, numbered in reading order, with the first line that names it
  const points = new Map<string, Point>();
  styled.forEach((b) => b.items.forEach((it) => {
    if (!points.has(it.key)) points.set(it.key, { ...rate(it), ...beerTaste(it), item: it, n: points.size + 1, index: b.index });
  }));
  const all = [...points.values()];
  const lit = new Set(styled.find((b) => b.index === selected)?.items.map((it) => it.key));
  const areas = all.some((p) => p.area);
  const byTaste = layout === 'taste';

  return (
    <Stack gap="md" className={classes.view}>
      <Box>
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
          <Text fw={700} size="lg">Beer styles</Text>
          <SegmentedControl
            value={layout}
            onChange={(v) => onLayout(v as BeerLayout)}
            data={[{ value: 'chart', label: 'Chart' }, { value: 'taste', label: 'By taste' }]}
            size="xs"
            radius="xl"
            aria-label="Show the styles on a chart, or grouped by taste"
          />
        </Group>
        <Text size="sm" c="dimmed" mt={4}>
          {byTaste
            ? `Each style sits with the taste that most sets it apart from other beer, the most intense first; each dot is the colour of the beer. ${areas ? 'A name like IPA covers several styles, so it goes by their average. ' : ''}Tap one to see what it is.`
            : `By how bitter and how full-bodied the style is; each dot is the colour of the beer. ${areas ? 'A name like IPA covers several styles, so its dot sits at their average. ' : ''}Tap one to see what it is.`}
        </Text>
      </Box>

      {all.length > 0 && (byTaste ? <Tastes points={all} lit={lit} onPick={onPick} /> : <Chart points={all} lit={lit} onPick={onPick} />)}

      <Rows
        items={styled}
        selected={selected}
        onPick={onPick}
        points={points}
        // The list rates each style on what the layout above is about
        axes={byTaste ? (it) => points.get(it.key)!.taste.axes : undefined}
        note={byTaste ? (it) => {
          const { taste, also } = points.get(it.key)!;
          return also ? `${taste.label}; also ${also.label.toLowerCase()}` : taste.label;
        } : undefined}
      />
      <Text size="xs" c="dimmed">
        Ratings are the guide’s judgement of the style in general, on a 0–5 scale; {byTaste ? 'a taste counts from 3 up' : 'strength follows the BJCP 2021 guidelines'}. A brewery’s own beer can differ.
      </Text>
    </Stack>
  );
};

interface RowsProps {
  items: BeerLine[];
  selected: number;
  onPick: (index: number) => void;
  /** Each style's number and the colour of its dot */
  points: Map<string, Point>;
  /** The ratings shown under a style, as indexes into BEER_AXES: the chart's two, unless the layout is about others */
  axes?: (it: BeerItem) => number[];
  /** A line above a style's ratings */
  note?: (it: BeerItem) => string;
}

const CHART_AXES = () => [BITTER, BODY];

/** A row per line, and in it a row per style the line names. */
const Rows: React.FC<RowsProps> = ({ items, selected, onPick, points, axes = CHART_AXES, note }) => (
  <ul className={classes.rows}>
    {items.map((b) => (
      <li key={b.index}>
        <UnstyledButton className={classes.row} onClick={() => onPick(b.index)} aria-current={b.index === selected}>
          <span className={classes.rowMain}>
            <Text component="span" fw={600} lang="ja" className={classes.printed}>{b.line.t}</Text>
            {b.items.map((it) => {
              const p = points.get(it.key)!;
              const ranges = beerRanges(it);
              return (
                <span key={it.key} className={classes.named}>
                  <DotKey color={p.color} n={p.n} />
                  <span className={classes.namedMain}>
                    <Text component="span" size="xs" c="dimmed">
                      {it.n}{it.kind === 'style' ? ` · ${it.f} · ${abvText(it)}` : ''}
                    </Text>
                    {note && <Text component="span" size="xs">{note(it)}</Text>}
                    <span className={classes.ratings}>
                      {axes(it).map((k) => (
                        <span key={k}><Text component="span" size="xs" c="dimmed" w={64} display="inline-block">{BEER_AXES[k].short}</Text><Pips range={ranges[k]} label={BEER_AXES[k].label} /></span>
                      ))}
                    </span>
                    {it.kind === 'word' && (
                      <Text component="span" size="xs" c="dimmed" fs="italic">
                        Can be several styles: {stylesOf(it).map((s) => s.n).join(', ')}; light dots depend on which
                      </Text>
                    )}
                  </span>
                </span>
              );
            })}
          </span>
          <IconChevronRight size={16} className={classes.chev} />
        </UnstyledButton>
      </li>
    ))}
  </ul>
);
